import { Popover } from "antd";
import { ItemWrapper } from "./Elements";
import { IconButton } from "@mui/material";
import {
  CircleOutlined,
  Create,
  MoreVert,
  Visibility,
  Delete,
  PowerSettingsNew,
  Close,
  LockOpen,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { Modal, Select } from "antd";
import {
  ACTIVATE,
  CLOSE,
  CLOSE_REASONS,
  DEACTIVATE,
  DELETE,
  EDIT,
  UNLOCK,
  VIEW,
} from "../../utils/constants";
import useSet from "../../hooks/common/useSet";
import { useToasts } from "../../hooks/common/useToasts";
import { useState } from "react";
import TextArea from "antd/es/input/TextArea";

type TActionProps = {
  label: string;
  to?: string;
  icon?: any;
  onClick?: () => void;
  deleteMessage?: string;
  deleteUrl?: string;
  custom?: boolean;
  handleCustom?: () => void;
  reasonField?: boolean;
};
type TFormActionsProps = {
  actions: TActionProps[];
};

function getIcon(label: string) {
  switch (label) {
    case VIEW:
      return <Visibility fontSize="small" />;
    case EDIT:
      return <Create fontSize="small" />;
    case DELETE:
      return <Delete fontSize="small" color="error" />;
    case ACTIVATE:
      return <PowerSettingsNew fontSize="small" color="success" />;
    case DEACTIVATE:
      return <PowerSettingsNew fontSize="small" color="error" />;
    case CLOSE:
      return <Close fontSize="small" color="warning" />;
    case UNLOCK:
      return <LockOpen fontSize="small" color="warning" />;
    default:
      return <CircleOutlined sx={{ fontSize: 8, width: 20 }} />;
  }
}

const handleDelete = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Delete",
    content: deleteMessage,
    okText: "Yes, Delete",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({ api: deleteUrl, method: "DELETE" });
    },
  });
};

const handleDeactivate = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Deactivate",
    content: deleteMessage,
    okText: "Yes, Deactivate",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({
        api: deleteUrl,
        method: "PATCH",
        params: { active: false },
      });
    },
  });
};

const handleUnlock = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Unlock",
    content: deleteMessage,
    okText: "Yes, Unlock",
    okType: "danger",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({ api: deleteUrl, method: "POST" });
    },
  });
};

const handleActivate = (
  deleteUrl: string,
  deleteMessage: string,
  onDelete: any,
) => {
  Modal.confirm({
    title: "Confirm Activate",
    content: deleteMessage,
    okText: "Yes, Activate",
    cancelText: "Cancel",
    onOk: async () => {
      onDelete({
        api: deleteUrl,
        method: "PATCH",
        params: { active: true },
      });
    },
  });
};

// const handleClose = (
//   deleteUrl: string,
//   deleteMessage: string,
//   onDelete: any,
// ) => {
//   Modal.confirm({
//     title: "Confirm Close",
//     content: deleteMessage,
//     okText: "Yes, Close",
//     okType: "danger",
//     cancelText: "Cancel",
//     onOk: async () => {
//       onDelete({ api: deleteUrl, method: "POST" });
//     },
//   });
// };

const CloseReasonModal = ({
  visible,
  deleteMessage,
  deleteUrl,
  onDelete,
  onCancel,
  loading,
  reasonField,
}: any) => {
  const [reasonValue, setReasonValue] = useState<string | undefined>();
  const [customReason, setCustomReason] = useState<string | undefined>();

  const handleOk = async () => {
    const finalReason = reasonValue === "other" ? customReason : reasonValue;
    onDelete({
      api: deleteUrl,
      method: "POST",
      params: reasonField ? { reason_for_closing: finalReason } : {},
    });
    onCancel();
  };

  return (
    <Modal
      title="Confirm Close"
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Yes, Close"
      // okType="danger"
      loading={loading}
      cancelText="Cancel"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <span>{deleteMessage}</span>

        {reasonField && (
          <>
            <Select
              placeholder="Select reason for closing"
              options={CLOSE_REASONS}
              onChange={(val) => {
                setReasonValue(val);
                setCustomReason(undefined);
              }}
            />
            {reasonValue === "other" && (
              <TextArea
                placeholder="Please enter your reason"
                onChange={(e) => setCustomReason(e.target.value)}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default CloseReasonModal;

export const ActionsMenu = (actions: any[]) => {
  const { uploadData: onDelete, success, error, submitting } = useSet();
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [closeModalData, setCloseModalData] = useState<any>(null);

  useToasts({
    success,
    error,
    redirect: "reload",
    successMessage: "Operation Successfull",
  });

  const handleOpenCloseModal = (
    deleteUrl: string,
    deleteMessage: string,
    reasonField: boolean,
  ) => {
    setCloseModalData({ deleteUrl, deleteMessage, reasonField });
    setCloseModalVisible(true);
  };

  return (
    <>
      <CloseReasonModal
        visible={closeModalVisible}
        deleteMessage={closeModalData?.deleteMessage}
        deleteUrl={closeModalData?.deleteUrl}
        reasonField={closeModalData?.reasonField}
        onDelete={onDelete}
        loading={submitting}
        onCancel={() => setCloseModalVisible(false)}
      />
      {actions?.map(
        (
          {
            label,
            to,
            onClick,
            icon: Icon,
            deleteMessage = "",
            deleteUrl = "",
            custom = false,
            handleCustom = () => {},
            reasonField = false,
          }: TActionProps,
          key: number,
        ) => (
          <Link
            to={to || ""}
            onClick={
              custom
                ? () => handleCustom && handleCustom()
                : label === DELETE
                  ? () => handleDelete(deleteUrl, deleteMessage, onDelete)
                  : label === DEACTIVATE
                    ? () => handleDeactivate(deleteUrl, deleteMessage, onDelete)
                    : label === ACTIVATE
                      ? () => handleActivate(deleteUrl, deleteMessage, onDelete)
                      : label === UNLOCK
                        ? () => handleUnlock(deleteUrl, deleteMessage, onDelete)
                        : label === CLOSE
                          ? (e: any) => {
                              e.preventDefault();
                              handleOpenCloseModal(
                                deleteUrl,
                                deleteMessage,
                                reasonField,
                              );
                            }
                          : onClick
            }
            key={key}
          >
            <ItemWrapper>
              {(Icon && <Icon />) || getIcon(label)}
              {label}
            </ItemWrapper>
          </Link>
        ),
      )}
    </>
  );
};

export const FormActions = ({ actions }: TFormActionsProps) => {
  return (
    <Popover
      content={<>{ActionsMenu(actions)}</>}
      trigger="click"
      overlayStyle={{ minWidth: 150 }}
      overlayInnerStyle={{ padding: 5 }}
      placement="left"
    >
      <IconButton size="small">
        <MoreVert />
      </IconButton>
    </Popover>
  );
};
